import type { CuratorOffer, CuratorOfferSubmissionPlaylist } from './curatorOffer';
import { isNewShapeComplete, selectionCoversAllOwned } from './curatorOffer';

export interface LinkedCuratorProfile {
  id: string;
  username: string;
  image?: string;
  acceptCampaigns?: boolean;
  curatorOffer?: CuratorOffer | null;
}

export interface ProfilePlaylistState {
  options: CuratorOfferSubmissionPlaylist[];
  selectedIds: string[];
  loading: boolean;
  error: string | null;
}

export interface ProfilePlaylistSelection {
  profileId: string;
  submissionPlaylists: CuratorOfferSubmissionPlaylist[];
  submissionPlaylistIds: string[];
  includeAllOwnedPlaylists?: boolean;
}

export interface AccountCuratorOfferSubmitPayload {
  accountUserId: string;
  sharedOffer: Omit<
    CuratorOffer,
    'submissionPlaylists' | 'submissionPlaylistIds' | 'includeAllOwnedPlaylists'
  >;
  profileOffers: ProfilePlaylistSelection[];
  disabledProfileIds: string[];
  email?: string;
  name?: string;
}

export const pickInitialSharedOffer = (
  profiles: LinkedCuratorProfile[],
  activeProfileId?: string | null
): CuratorOffer | null => {
  const activeProfile = profiles.find(profile => profile.id === activeProfileId);
  if (activeProfile?.curatorOffer && isNewShapeComplete(activeProfile.curatorOffer)) {
    return activeProfile.curatorOffer;
  }

  const completeOffers = profiles
    .map(profile => profile.curatorOffer)
    .filter(offer => isNewShapeComplete(offer)) as CuratorOffer[];

  if (!completeOffers.length) {
    return activeProfile?.curatorOffer || profiles[0]?.curatorOffer || null;
  }

  return [...completeOffers].sort((a, b) => {
    const aTime = new Date(a.updatedAt || 0).getTime();
    const bTime = new Date(b.updatedAt || 0).getTime();
    return bTime - aTime;
  })[0];
};

export const getInitialSelectedProfileIds = (profiles: LinkedCuratorProfile[]): string[] => {
  const openProfiles = profiles.filter(profile => profile.acceptCampaigns).map(profile => profile.id);
  if (openProfiles.length) return openProfiles;
  return profiles.map(profile => profile.id);
};

export const accountIsOpenForCampaigns = (profiles: LinkedCuratorProfile[]): boolean =>
  profiles.some(profile => profile.acceptCampaigns);

export const accountHasIncompleteOffer = (profiles: LinkedCuratorProfile[]): boolean =>
  profiles.some(
    profile => profile.acceptCampaigns && !isNewShapeComplete(profile.curatorOffer || null)
  );

export const buildSharedOfferFields = (
  offer: CuratorOffer | null | undefined,
  options?: { offerTemplateId?: string | null }
) => ({
  dailyStreamsMin: offer?.dailyStreamsMin || 0,
  dailyStreamsMax: offer?.dailyStreamsMax || 0,
  defaultRatePer1k: offer?.defaultRatePer1k || 0,
  notes: offer?.notes || '',
  genres: Array.isArray(offer?.genres) ? [...offer.genres] : [],
  interestedInFeedback:
    typeof offer?.interestedInFeedback === 'boolean'
      ? offer.interestedInFeedback
      : (undefined as unknown as boolean),
  audienceCountryCodes: Array.isArray(offer?.audienceCountryCodes)
    ? [...offer.audienceCountryCodes]
    : [],
  audienceCountriesUnknown: offer?.audienceCountriesUnknown === true,
  offerTemplateId:
    options?.offerTemplateId ??
    (typeof offer?.offerTemplateId === 'string' ? offer.offerTemplateId : undefined),
});

export const buildProfileOfferFromSaved = (
  profile: LinkedCuratorProfile
): ProfilePlaylistSelection => ({
  profileId: profile.id,
  submissionPlaylists: profile.curatorOffer?.submissionPlaylists || [],
  submissionPlaylistIds: profile.curatorOffer?.submissionPlaylistIds || [],
  includeAllOwnedPlaylists: profile.curatorOffer?.includeAllOwnedPlaylists === true,
});

export const resolveInitialProfilePlaylists = (
  owned: CuratorOfferSubmissionPlaylist[],
  savedOffer?: CuratorOffer | null
) => {
  const savedIds = [
    ...(savedOffer?.submissionPlaylistIds || []),
    ...(savedOffer?.submissionPlaylists || []).map(playlist => playlist.playlistId),
  ].filter(Boolean);
  const savedIdSet = new Set(savedIds);
  const useAllOwned =
    savedOffer?.includeAllOwnedPlaylists === true || savedIdSet.size === 0;

  const selectedPlaylists = useAllOwned
    ? owned.map(playlist => ({ ...playlist }))
    : owned.filter(playlist => savedIdSet.has(playlist.playlistId)).map(playlist => ({ ...playlist }));

  return { selectedPlaylists };
};

export const buildAllOwnedProfileOfferUpdate = (
  profile: LinkedCuratorProfile,
  owned: CuratorOfferSubmissionPlaylist[]
): ProfilePlaylistSelection | null => {
  if (profile.curatorOffer?.includeAllOwnedPlaylists !== true) {
    return null;
  }

  const ownedIds = owned.map(playlist => playlist.playlistId);
  const savedIds = [
    ...(profile.curatorOffer?.submissionPlaylistIds || []),
    ...(profile.curatorOffer?.submissionPlaylists || []).map(playlist => playlist.playlistId),
  ].filter(Boolean);
  const savedSet = new Set(savedIds);
  const hasNew = ownedIds.some(id => !savedSet.has(id));
  const hasRemoved = savedIds.some(id => !ownedIds.includes(id));

  if (!(hasNew || hasRemoved) || ownedIds.length === 0) {
    return null;
  }

  return {
    profileId: profile.id,
    submissionPlaylists: owned,
    submissionPlaylistIds: ownedIds,
    includeAllOwnedPlaylists: selectionCoversAllOwned(ownedIds, ownedIds),
  };
};

export const syncAllOwnedProfileOffers = async ({
  profiles,
  accountUserId,
  activeProfileId,
  sharedOffer,
  fetchOwnedForActive,
  fetchOwnedForProfile,
  saveOffers,
}: {
  profiles: LinkedCuratorProfile[];
  accountUserId: string;
  activeProfileId: string;
  sharedOffer: CuratorOffer | null;
  fetchOwnedForActive: () => Promise<CuratorOfferSubmissionPlaylist[]>;
  fetchOwnedForProfile: (profileId: string) => Promise<CuratorOfferSubmissionPlaylist[]>;
  saveOffers: (payload: AccountCuratorOfferSubmitPayload) => Promise<unknown>;
}): Promise<boolean> => {
  const openProfiles = profiles.filter(profile => profile.acceptCampaigns);
  let hasSyncChanges = false;

  const profileOffers = await Promise.all(
    openProfiles.map(async profile => {
      if (profile.curatorOffer?.includeAllOwnedPlaylists !== true) {
        return buildProfileOfferFromSaved(profile);
      }

      try {
        const owned =
          profile.id === activeProfileId
            ? await fetchOwnedForActive()
            : await fetchOwnedForProfile(profile.id);
        const syncedOffer = buildAllOwnedProfileOfferUpdate(profile, owned);
        if (syncedOffer) {
          hasSyncChanges = true;
          return syncedOffer;
        }
      } catch (syncError) {
        console.error('Failed to sync all-owned playlists for curator offer:', syncError);
      }

      return buildProfileOfferFromSaved(profile);
    })
  );

  if (!hasSyncChanges) {
    return false;
  }

  await saveOffers({
    accountUserId,
    sharedOffer: buildSharedOfferFields(sharedOffer),
    profileOffers,
    disabledProfileIds: profiles.filter(profile => !profile.acceptCampaigns).map(profile => profile.id),
  });

  return true;
};
